import clsx from "clsx";
import { type FC, useEffect } from "react";
import { useBoolean } from "usehooks-ts";

import { AddCourseGroupingModal } from "./AddCourseGroupingModal";
import { showAddCourseModal } from "./AddOrRenameCourseModalUtils";
import { CourseNavListItem } from "./CourseNavListItem";

import { COURSE_GROUPING_ID_QUERY_PARAM, COURSE_ID_QUERY_PARAM } from "components/constants";
import {
  type SectionViewOption,
  SectionViewOptionMenu,
} from "components/menus/SectionViewOptionMenu";
import { t } from "i18n/i18n";
import { Dialog } from "mds/components/Dialog";
import { IconText } from "mds/components/IconText";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { AddPlusIcon, FolderIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  courseGroupingSelectors,
  courseSelectors,
  selectAdminAccessibleCourseIdsByGroupingId,
  selectCourseGroupingIdsWithCourses,
  selectIsEditingAsOrgAdmin,
  selectUngroupedCourseIds,
} from "store/selectors";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import { ADMIN_COURSE_EVENT_PREFIX } from "utils/amplitude";
import { useQueryParam, useQueryParamsActions } from "utils/routing";

type CourseNavGroupingItemProps = {
  groupingId: string;
  isSelected: boolean;
  isExpanded: boolean;
  selectedCourseId: string | null;
  isCourseSelectionActive: boolean;
};

const CourseNavGroupingItem: FC<CourseNavGroupingItemProps> = ({
  groupingId,
  isSelected,
  isExpanded,
  selectedCourseId,
  isCourseSelectionActive,
}) => {
  const { setQueryParams } = useQueryParamsActions();
  const grouping = useObjectSelector(
    (state) => courseGroupingSelectors.selectById(state, groupingId),
    ["title"],
  );
  const courseIds = useListSelector((state) =>
    selectAdminAccessibleCourseIdsByGroupingId(state, groupingId),
  );

  if (!grouping) {
    return null;
  }

  return (
    <div>
      <div
        className="my-1 flex min-w-12 cursor-pointer items-center justify-between rounded-lg px-2 hover:bg-black-tint-97 data-[selected]:border-blue-tint-40 data-[selected]:bg-blue data-[selected]:text-white"
        data-selected={isSelected || undefined}
        onClick={() =>
          setQueryParams({
            [COURSE_GROUPING_ID_QUERY_PARAM]: groupingId,
            [COURSE_ID_QUERY_PARAM]: null,
          })
        }
      >
        <div className="flex items-center gap-2">
          <FolderIcon />

          <h3 className="body-s my-1 w-full min-w-12 truncate font-medium">{grouping.title}</h3>
        </div>
      </div>

      {isExpanded &&
        courseIds.map((id) => (
          <CourseNavListItem
            className="ml-6"
            courseId={id}
            isSelected={isCourseSelectionActive && selectedCourseId === id}
            key={id}
          />
        ))}
    </div>
  );
};

type CourseNavListProps = {
  onOptionChange: (option: SectionViewOption) => void;
};

export const CourseNavList: FC<CourseNavListProps> = ({ onOptionChange }) => {
  const groupingIds = useListSelector(selectCourseGroupingIdsWithCourses);
  const ungroupedCourseIds = useListSelector(selectUngroupedCourseIds);
  const { setQueryParams } = useQueryParamsActions();
  const courseId = useQueryParam(COURSE_ID_QUERY_PARAM);
  const groupingId = useQueryParam(COURSE_GROUPING_ID_QUERY_PARAM);
  const canEdit = useAppSelector(selectIsEditingAsOrgAdmin);
  const isSmallScreen = !useIsMdOrLarger();
  const currentCourseGroupingId = useObjectSelector(
    (state) => courseSelectors.selectById(state, courseId),
    ["grouping_id"],
  )?.grouping_id;

  const firstGroupId = groupingIds[0];
  const firstCourseId = ungroupedCourseIds[0];
  const hasCourses = groupingIds.length > 0 || ungroupedCourseIds.length > 0;
  const isCourseSelectionActive = !groupingId || !groupingIds.includes(groupingId);

  // Set default query params if neither grouping_id nor course_id are set
  useEffect(() => {
    if (!groupingId && !courseId) {
      if (firstGroupId) {
        setQueryParams({
          [COURSE_GROUPING_ID_QUERY_PARAM]: firstGroupId,
          [COURSE_ID_QUERY_PARAM]: null,
        });
      } else if (firstCourseId) {
        setQueryParams({
          [COURSE_ID_QUERY_PARAM]: firstCourseId,
          [COURSE_GROUPING_ID_QUERY_PARAM]: null,
        });
      }
    }
  }, [groupingId, courseId, firstGroupId, firstCourseId, setQueryParams]);

  const { value: dialogIsOpen, setTrue: openDialog, setFalse: closeDialog } = useBoolean(false);

  return (
    <div
      className={clsx(
        "grid max-h-[calc(100%-24px)] w-[240px] grid-rows-[auto_1fr_auto] rounded-2xl border border-black-tint-90 p-4",
        isSmallScreen && "w-full",
      )}
      role="list"
    >
      <div className="mb-4">
        <SectionViewOptionMenu
          eventPrefix={ADMIN_COURSE_EVENT_PREFIX}
          selectedOption="course"
          onOptionChange={onOptionChange}
        />
      </div>

      <div className="min-h-0 overflow-y-auto">
        {groupingIds.map((id) => {
          const isSelected = groupingId === id;
          return (
            <CourseNavGroupingItem
              groupingId={id}
              isCourseSelectionActive={isCourseSelectionActive}
              isExpanded={isSelected || currentCourseGroupingId === id}
              isSelected={isSelected}
              key={id}
              selectedCourseId={courseId}
            />
          );
        })}

        {ungroupedCourseIds.map((id) => (
          <CourseNavListItem
            courseId={id}
            isSelected={isCourseSelectionActive && courseId === id}
            key={id}
          />
        ))}
      </div>

      {canEdit && (
        <>
          <SquareButton
            className={clsx("w-full", hasCourses && "mt-2")}
            eventName={`${ADMIN_COURSE_EVENT_PREFIX}Add Course`}
            kind="tertiary"
            size="s"
            title={t("common.add.course")}
            onClick={() => showAddCourseModal()}
          >
            <IconText iconStart={<AddPlusIcon />} text={t("common.add.course")} />
          </SquareButton>

          <SquareButton
            className="mt-2 w-full"
            eventName={`${ADMIN_COURSE_EVENT_PREFIX}Add Course Grouping`}
            kind="tertiary"
            size="s"
            title={t("common.add.course_grouping")}
            onClick={openDialog}
          >
            <IconText iconStart={<FolderIcon />} text={t("common.add.course_grouping")} />
          </SquareButton>
          <Dialog
            open={dialogIsOpen}
            title={t("home.courses.add_course_grouping_heading")}
            onClose={closeDialog}
          >
            <AddCourseGroupingModal onClose={closeDialog} />
          </Dialog>
        </>
      )}
    </div>
  );
};
